import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Controller, useForm } from 'react-hook-form';
import { AppDispatch } from '../../state';
import { getDirtyFormValues } from '../../utils/form';
import { setNotificationError, setNotificationSuccess } from '../../state/reducers/notification';
import { FetchApi } from '../../utils/fetch/fetchApi';
import { paths, pathsApi } from '../../router/paths';
import Body from '../../ui/Body/body';
import GridRow from '../../ui/GridRow';
import GridCol from '../../ui/GridCol';
import Input from '../../ui/Input';
import ToggleButton from '../../ui/ToggleButton/ToggleButton';
import sCommon from '../../utils/common.module.scss';
import Button from '../../ui/Button';
import { ButtonVariationEnum } from '../../ui/Button/types';
import { parseHistoryState } from './utils/parseHistoryState';
import { ICategoryForm, ICategorySaveDto } from './types';
import SeoProduct from '../../components/SeoProduct';
import Details from '../../ui/Details/details';
import { IconEnum } from '../../ui/Icon';
import Dropdown from '../../ui/Dropdown';
import { convertArray } from '../../utils/convertArray';
import { useConfig } from '../../components/Config/config';
import AliasLink from '../../components/AliasLink';
import i18n from '../../utils/i18n';
import ImagesUploadCategory from './modules/ImagesUploadCategory';
import CategoryFilters from './modules/CategoryFilters';
import CategoryButtons from './modules/CategoryButtons';
import { GridAlignEnum } from '../../ui/GridRow/GridRow';

const CategoryNew = () => {
    const { config } = useConfig();
    const dispatch = useDispatch<AppDispatch>();
    const defaultValues = parseHistoryState();
    const {
        control,
        reset,
        handleSubmit,
        setValue,
        formState: { dirtyFields },
    } = useForm<ICategoryForm>({ defaultValues });
    const navigate = useNavigate();
    const [isLoading, toggleLoading] = useState<boolean>(false);

    const submit = handleSubmit(async (data) => {
        // toggleLoading(true);
        const payload = defaultValues?.id ? getDirtyFormValues<ICategoryForm>({ formFields: data, dirtyFields }) : data;

        if (!payload) {
            dispatch(setNotificationError(i18n.general.noChanges));
            toggleLoading(false);
            return;
        }

        const serializedData: Partial<ICategorySaveDto> = {
            id: data.id,
            ...payload,
            img: payload.img && [payload.img.src, payload.img.alt, payload.img.title],
            parent: payload.parent?.id,
            keywords: payload.keywords?.join(','),
            buttons: payload.buttons?.map((item) =>
                [
                    item.name,
                    new URL(item.link, globalThis.location.origin).pathname,
                    item.img.src,
                    item.img.alt,
                    item.img.title,
                ].join('::'),
            ),
        };

        const response = await FetchApi.post<ICategorySaveDto>(pathsApi.categorySave, serializedData);
        if (response?.id) {
            dispatch(
                setNotificationSuccess(
                    defaultValues?.id ? i18n.general.updatedSuccessful : i18n.general.successFullyAdded,
                ),
            );
            navigate(paths.category + response.id, { state: { category: response } });
        } else {
            toggleLoading(false);
        }
    });

    return (
        <Body title={defaultValues?.id ? i18n.category.updateCategory : i18n.menu.categoryNew}>
            <GridRow gutter={8}>
                <GridCol col={8}>
                    <GridRow gutter={8} alignRow={GridAlignEnum.TOP}>
                        <GridCol col={6}>
                            <Controller
                                name={'name'}
                                control={control}
                                rules={{ required: true }}
                                defaultValue={''}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <Input
                                        value={value}
                                        onChange={onChange}
                                        label={i18n.general.name}
                                        error={!!error}
                                    />
                                )}
                            />
                        </GridCol>
                        <GridCol col={6}>
                            <Controller
                                name={'parent'}
                                control={control}
                                rules={{ required: true }}
                                render={({ field: { onChange, value }, fieldState: { error } }) => (
                                    <Dropdown
                                        value={value as { id: number }}
                                        emptyPlaceholder
                                        isValueObject
                                        onChange={onChange}
                                        options={convertArray(
                                            config.categories.map((item) => ({
                                                ...item,
                                                name: ' - '.repeat(item.depth) + item.name,
                                            })),
                                        )}
                                        error={!!error}
                                        warningMessage={
                                            defaultValues?.id ? i18n.category.categoryMoveWarning : undefined
                                        }
                                        label={i18n.category.parentCategory}
                                    />
                                )}
                            />
                        </GridCol>
                        <GridCol col={6}>
                            <AliasLink control={control} name={'alias'} setValue={setValue} />
                        </GridCol>
                        <GridCol col={6}>
                            <Controller
                                name={'visible'}
                                control={control}
                                defaultValue={true}
                                render={({ field: { onChange, value } }) => (
                                    <ToggleButton value={value} onClick={onChange} label={i18n.general.active} />
                                )}
                            />
                        </GridCol>
                    </GridRow>
                </GridCol>
                <GridCol col={12}>
                    <ImagesUploadCategory control={control} />
                    <Details label={i18n.category.description} icon={IconEnum.DOCS}>
                        <Controller
                            name={'description'}
                            control={control}
                            defaultValue={''}
                            render={({ field: { onChange, value }, fieldState: { error } }) => (
                                <Input
                                    isMultiLine
                                    value={value}
                                    onChange={onChange}
                                    error={!!error}
                                    errorMessage={error?.message}
                                    label={i18n.general.description}
                                />
                            )}
                        />
                    </Details>
                    <CategoryFilters control={control} />
                    <CategoryButtons control={control} />
                    <SeoProduct control={control} />
                </GridCol>
                <GridCol col={12} className={sCommon.buttonWrapper}>
                    <Button onClick={submit} isLoading={isLoading}>
                        {i18n.general.save}
                    </Button>
                    <Button variation={ButtonVariationEnum.SOFT} onClick={() => reset()}>
                        {i18n.general.reset}
                    </Button>
                </GridCol>
            </GridRow>
        </Body>
    );
};

export default CategoryNew;
